import { useState } from "react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, TextInput, SpecialistsEditor } from "@/components/common/Forms";
import { ProtocolChecks } from "@/pages/admin/center/ApprovalPanel";
import { api, errMsg } from "@/lib/api";

const FIELDS = [
  ["center_name", "Nome del centro"], ["company_name", "Ragione sociale"], ["vat_number", "Partita IVA"], ["tax_code", "Codice fiscale"],
  ["sdi_code", "Codice SDI"], ["pec", "PEC"], ["address", "Indirizzo"], ["zip_code", "CAP"], ["city", "Città"], ["province", "Provincia"],
  ["owner_first_name", "Nome titolare"], ["owner_last_name", "Cognome titolare"], ["phone", "Telefono"], ["email", "Email (accesso)"],
  ["instagram", "Instagram"], ["website", "Sito web"],
  ["sales_rep_name", "Commerciale di riferimento"], ["sales_rep_email", "Email del commerciale"],
  ["shopify_customer_id", "Shopify Customer ID"],
];

export default function CenterInfoForm({ center, lists, onSaved }) {
  const [f, setF] = useState(() => ({
    ...Object.fromEntries(FIELDS.map(([k]) => [k, center[k] || ""])),
    client_type_id: center.client_type_id || "", enabled_protocols: center.enabled_protocols || [], specialists: center.specialists || [],
    contract_type: center.contract_type || "none", contract_start: center.contract_start || "", contract_count_from: center.contract_count_from || "", contract_annual_target: center.contract_annual_target ?? "",
  }));
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (f.specialists.some((s) => !s.first_name.trim() || !s.last_name.trim())) return toast.error("Completa nome e cognome di ogni specialist");
    setBusy(true);
    const body = { ...f, contract_annual_target: f.contract_annual_target === "" ? null : Number(f.contract_annual_target) };
    if (f.contract_type !== "none" && (!f.contract_start || !body.contract_annual_target)) {
      setBusy(false);
      return toast.error("Per il contratto indica data di inizio e cifra annuale");
    }
    if (!body.client_type_id) delete body.client_type_id;
    try {
      const r = await api.patch(`/admin/centers/${center.id}`, body);
      toast.success("Scheda aggiornata");
      onSaved(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="space-y-8" data-testid="center-info-form">
      <section className="lux-card p-6 sm:p-8">
        <h3 className="text-xl font-normal mb-6">Dati anagrafici</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FIELDS.map(([k, l]) => (
            <Field key={k} label={l}>
              <TextInput data-testid={`center-field-${k.replace(/_/g, "-")}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={k === "shopify_customer_id" ? "Se vuoto, cerchiamo per email" : ""} />
            </Field>
          ))}
        </div>
      </section>
      <section className="lux-card p-6 sm:p-8 space-y-6" data-testid="center-contract-section">
        <h3 className="text-xl font-normal">Contratto e obiettivo di spesa</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <Field label="Tipo di contratto">
            <Select value={f.contract_type} onValueChange={(v) => setF({ ...f, contract_type: v })}>
              <SelectTrigger data-testid="center-contract-type" className="bg-ivory-deep rounded-xl h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessun contratto</SelectItem>
                <SelectItem value="agevolazione">Contratto di agevolazione</SelectItem>
                <SelectItem value="official_partner">Centro Official Partner</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Data di inizio"><TextInput type="date" data-testid="center-contract-start" value={f.contract_start} onChange={(e) => setF({ ...f, contract_start: e.target.value })} /></Field>
          <Field label="Conta gli ordini dal" hint="Facoltativo: se vuoto, dalla data di inizio"><TextInput type="date" data-testid="center-contract-count-from" value={f.contract_count_from} onChange={(e) => setF({ ...f, contract_count_from: e.target.value })} /></Field>
          <Field label="Cifra annuale da raggiungere (€, netto)"><TextInput type="number" min="0" step="0.01" data-testid="center-contract-target" value={f.contract_annual_target} onChange={(e) => setF({ ...f, contract_annual_target: e.target.value })} /></Field>
        </div>
        <p className="text-xs text-warm-text">La spesa si calcola dagli ordini Shopify del cliente (Shopify Customer ID sopra), senza IVA e senza spedizione. Per gli Official Partner la cifra viene divisa in 12 quote mensili. Gli ordini fatti da «Conta gli ordini dal» valgono per il primo mese del contratto.</p>
      </section>
      <section className="lux-card p-6 sm:p-8 space-y-6">
        <h3 className="text-xl font-normal">Tipologia e protocolli</h3>
        <Field label="Tipologia cliente">
          <Select value={f.client_type_id} onValueChange={(v) => setF({ ...f, client_type_id: v })}>
            <SelectTrigger data-testid="center-client-type-select" className="bg-ivory-deep rounded-xl h-11 sm:max-w-sm"><SelectValue placeholder="Non assegnata" /></SelectTrigger>
            <SelectContent>{lists["client-types"].map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{!t.active && " (disattivata)"}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Protocolli abilitati">
          <ProtocolChecks protocols={lists.protocols} value={f.enabled_protocols} onChange={(v) => setF({ ...f, enabled_protocols: v })} testid="center-protocol" />
        </Field>
      </section>
      <section className="lux-card p-6 sm:p-8">
        <h3 className="text-xl font-normal mb-6">Specialist</h3>
        <SpecialistsEditor value={f.specialists} onChange={(v) => setF({ ...f, specialists: v })} testid="center-specialists" />
      </section>
      <button data-testid="center-save-btn" disabled={busy} className="btn-gold">{busy ? "Salvataggio…" : "Salva la scheda"}</button>
    </form>
  );
}
